import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config';
import '../styles/admin.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatLastActive(date) {
    if (!date) return 'Never';
    const diffMs = Date.now() - new Date(date).getTime();
    const mins  = Math.floor(diffMs / 60_000);
    const hours = Math.floor(diffMs / 3_600_000);
    const days  = Math.floor(diffMs / 86_400_000);
    if (mins  <  1)  return 'Just now';
    if (mins  < 60)  return `${mins}m ago`;
    if (hours < 24)  return `${hours}h ago`;
    if (days  <  7)  return `${days}d ago`;
    if (days  < 30)  return `${Math.floor(days / 7)}w ago`;
    return new Date(date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

function activityStatus(lastLogin) {
    if (!lastLogin) return 'dormant';
    const days = (Date.now() - new Date(lastLogin).getTime()) / 86_400_000;
    if (days <  1) return 'today';
    if (days <  7) return 'week';
    if (days < 30) return 'month';
    return 'dormant';
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminPowerUsers() {
    const [users,          setUsers]          = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [error,          setError]          = useState('');
    const [success,        setSuccess]        = useState('');
    const [searchTerm,     setSearchTerm]     = useState('');
    const [filterPower,    setFilterPower]    = useState('all');
    const [filterActivity, setFilterActivity] = useState('all');
    const [sortBy,         setSortBy]         = useState('username');
    const [updatingUserId, setUpdatingUserId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const username    = localStorage.getItem('username');
        const isPowerUser = localStorage.getItem('isPowerUser') === 'true';
        if (username !== 'Higgo' || !isPowerUser) { navigate('/'); return; }
        fetchUsers();
    }, [navigate]);

    useEffect(() => () => { setError(''); setSuccess(''); }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axiosInstance.get('/users/admin/all-users');
            setUsers(res.data.users || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const togglePowerUser = async (userId, currentStatus) => {
        try {
            setError('');
            setSuccess('');
            setUpdatingUserId(userId);
            const res = await axiosInstance.put(`/users/admin/toggle-power-user/${userId}`, {
                isPowerUser: !currentStatus
            });
            if (res.data.success) {
                setUsers(prev => prev.map(u =>
                    u._id === userId ? { ...u, isPowerUser: !currentStatus } : u
                ));
                const user = users.find(u => u._id === userId);
                setSuccess(`${user?.username || 'User'} ${!currentStatus ? 'granted' : 'removed'} power user status`);
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update user status');
        } finally {
            setUpdatingUserId(null);
        }
    };

    // ── Derived stats ──────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const activeToday   = users.filter(u => activityStatus(u.lastLogin) === 'today').length;
        const activeMonth   = users.filter(u => u.logins30Days > 0).length;
        const neverLoggedIn = users.filter(u => !u.lastLogin).length;
        return { activeToday, activeMonth, neverLoggedIn };
    }, [users]);

    // ── Filter + sort ──────────────────────────────────────────────────────
    const filteredUsers = useMemo(() => {
        let list = users.filter(u => {
            const q = searchTerm.toLowerCase();
            const matchSearch =
                !q ||
                u.username?.toLowerCase().includes(q) ||
                u.email?.toLowerCase().includes(q) ||
                u.firstName?.toLowerCase().includes(q) ||
                u.lastName?.toLowerCase().includes(q);

            const matchPower =
                filterPower === 'all' ||
                (filterPower === 'power'   &&  u.isPowerUser) ||
                (filterPower === 'regular' && !u.isPowerUser);

            const status = activityStatus(u.lastLogin);
            const matchActivity =
                filterActivity === 'all'   ||
                (filterActivity === 'today'  && status === 'today')  ||
                (filterActivity === 'week'   && (status === 'today' || status === 'week')) ||
                (filterActivity === 'month'  && u.logins30Days > 0)  ||
                (filterActivity === 'dormant'&& !u.lastLogin);

            return matchSearch && matchPower && matchActivity;
        });

        list = [...list].sort((a, b) => {
            switch (sortBy) {
                case 'lastActive':
                    return (new Date(b.lastLogin || 0)) - (new Date(a.lastLogin || 0));
                case 'logins30Days':
                    return (b.logins30Days || 0) - (a.logins30Days || 0);
                case 'recipeCount':
                    return (b.recipeCount || 0) - (a.recipeCount || 0);
                case 'joined':
                    return (new Date(b.createdAt || 0)) - (new Date(a.createdAt || 0));
                default:
                    return (a.username || '').localeCompare(b.username || '');
            }
        });

        return list;
    }, [users, searchTerm, filterPower, filterActivity, sortBy]);

    if (loading) {
        return (
            <div className="admin-container">
                <div className="loading-spinner">Loading users…</div>
            </div>
        );
    }

    return (
        <div className="admin-container">
            <div className="admin-header">
                <h1>Power User Management</h1>
                <p className="admin-subtitle">Manage users and monitor platform activity</p>
            </div>

            {error && (
                <div className="admin-error">
                    {error}
                    <button onClick={() => setError('')} className="error-close-btn" aria-label="Close">×</button>
                </div>
            )}
            {success && (
                <div className="admin-success">
                    {success}
                    <button onClick={() => setSuccess('')} className="success-close-btn" aria-label="Close">×</button>
                </div>
            )}

            {/* ── Stats ─────────────────────────────────────────────────── */}
            <div className="admin-stats">
                <div className="stat-card">
                    <div className="stat-number">{users.length}</div>
                    <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-card stat-card--green">
                    <div className="stat-number">{users.filter(u => u.isPowerUser).length}</div>
                    <div className="stat-label">Power Users</div>
                </div>
                <div className="stat-card stat-card--grey">
                    <div className="stat-number">{users.filter(u => !u.isPowerUser).length}</div>
                    <div className="stat-label">Regular Users</div>
                </div>
                <div className="stat-card stat-card--teal">
                    <div className="stat-number">{stats.activeToday}</div>
                    <div className="stat-label">Active Today</div>
                </div>
                <div className="stat-card stat-card--amber">
                    <div className="stat-number">{stats.activeMonth}</div>
                    <div className="stat-label">Active (30 days)</div>
                </div>
                <div className="stat-card stat-card--red">
                    <div className="stat-number">{stats.neverLoggedIn}</div>
                    <div className="stat-label">Never Logged In</div>
                </div>
            </div>

            {/* ── Controls ──────────────────────────────────────────────── */}
            <div className="admin-controls">
                <div className="admin-search">
                    <input
                        type="text"
                        placeholder="Search by username, email or name…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="admin-search-input"
                    />
                </div>
                <div className="admin-filter">
                    <label>Role:</label>
                    <select
                        value={filterPower}
                        onChange={e => setFilterPower(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">All Users</option>
                        <option value="power">Power Users</option>
                        <option value="regular">Regular Users</option>
                    </select>
                </div>
                <div className="admin-filter">
                    <label>Activity:</label>
                    <select
                        value={filterActivity}
                        onChange={e => setFilterActivity(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">All Activity</option>
                        <option value="today">Active Today</option>
                        <option value="week">Active This Week</option>
                        <option value="month">Active This Month</option>
                        <option value="dormant">Never Logged In</option>
                    </select>
                </div>
                <div className="admin-filter">
                    <label>Sort:</label>
                    <select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="username">Username</option>
                        <option value="lastActive">Last Active</option>
                        <option value="logins30Days">30d Sessions</option>
                        <option value="recipeCount">Recipes</option>
                        <option value="joined">Newest</option>
                    </select>
                </div>
            </div>

            {/* ── Table ─────────────────────────────────────────────────── */}
            <div className="admin-users-table">
                <table>
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Recipes</th>
                            <th>Last Active</th>
                            <th>30d Sessions</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="no-results">No users found matching your criteria</td>
                            </tr>
                        ) : filteredUsers.map(user => {
                            const status = activityStatus(user.lastLogin);
                            return (
                                <tr key={user._id}>
                                    <td className="username-cell">
                                        <span className={`activity-dot activity-dot--${status}`} title={`Status: ${status}`} />
                                        <strong>{user.username}</strong>
                                        {user.username === 'Higgo' && (
                                            <span className="admin-badge">Admin</span>
                                        )}
                                    </td>
                                    <td>{user.firstName} {user.lastName}</td>
                                    <td>{user.email}</td>
                                    <td>
                                        {user.isPowerUser
                                            ? <span className="power-user-badge">Power User</span>
                                            : <span className="regular-user-badge">Regular</span>}
                                    </td>
                                    <td>{user.recipeCount || 0}</td>
                                    <td className="last-active-cell">
                                        <span className={`last-active-text last-active-text--${status}`}>
                                            {formatLastActive(user.lastLogin)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`sessions-badge sessions-badge--${
                                            user.logins30Days >= 10 ? 'high' :
                                            user.logins30Days >= 3  ? 'mid'  : 'low'
                                        }`}>
                                            {user.logins30Days || 0}
                                        </span>
                                    </td>
                                    <td>
                                        {user.username !== 'Higgo' && (
                                            <button
                                                onClick={() => togglePowerUser(user._id, user.isPowerUser)}
                                                className={`toggle-button ${user.isPowerUser ? 'remove-power' : 'grant-power'}`}
                                                disabled={updatingUserId === user._id}
                                            >
                                                {updatingUserId === user._id
                                                    ? <span className="button-loading">Updating…</span>
                                                    : user.isPowerUser ? 'Remove Power' : 'Grant Power'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <p className="activity-note">
                * Activity data is recorded from the date this feature was enabled. Users who have not logged in since will show "Never".
            </p>
        </div>
    );
}
